import { ColumnDetails, ColumnKeys } from '@nirvana/shared/dtos/types/alerts/common';
import { getFormattedDateString } from '../../grid/utils/renderers';
import { ActionTakenEnum } from '@nirvana/shared/dtos/types/shared/threats';
import {
  RawEventDeviceDetails,
  RawEventPolicyDetails,
  RawEventRiskManagementDetails,
  RawEventTag,
} from '@nirvana/shared/dtos/types/raw-events/gz-console';
import { LinkColor, LinkType } from '../../ui/gzLinkComponent/types';
import { RoutinesSeverity } from '../types';
import { GzLinkWebComponent } from '../../ui/gzLinkComponent';
import { ActionPermissionState, DetailsPanelEvents, SecurityRisksDrillDown } from '../../alertsDetailsPanel/types';
import { ButtonHelper } from '../../alertsDetailsPanel/helpers/ButtonHelper';
import { GzClipContentWebComponent } from '../../index.ui';
import { ColumnKeys as DevicesAppColumnKeys } from '../../riskManagement/devicesApp/types';
import { ColumnKeys as MisconfigurationAppColumnKeys } from '../../riskManagement/misconfigurationsApp/types';
import { translateFn } from '../../utils/translator.helper';
import { htmlEncode } from '../../utils/escape.utils';

export class ContentRendererHelper {
  private _allColumnDetails: Array<ColumnDetails>;
  private dependencies;
  private buttonHelper: ButtonHelper;

  constructor(dependencies) {
    this.dependencies = dependencies;
    this.buttonHelper = new ButtonHelper(this.dependencies);
  }

  public get allColumnDetails(): Array<ColumnDetails> {
    return this._allColumnDetails;
  }

  public set allColumnDetails(value: Array<ColumnDetails>) {
    this._allColumnDetails = value;
  }

  public getContentLabelFromValue(searchValue: number | undefined, columnKey: ColumnKeys): string {
    if (searchValue === undefined) {
      return '-';
    }

    const columnDetails = this.allColumnDetails.find((colDetails: ColumnDetails) => colDetails.key === columnKey);

    if (!columnDetails) {
      throw new Error(`Unknown column ${columnKey}`);
    }

    if (!columnDetails.values) {
      throw new Error('Missing required column details values');
    }

    const value = columnDetails.values.find(columnValue => columnValue.value === searchValue);
    if (!value) {
      throw new Error(`Unknown value ${searchValue} for column type ${columnDetails.key}`);
    }

    return value.label;
  }

  public getLastDetectedOnValue(value: { low: number; high: number; unsigned: boolean }, timezone: string): string {
    return getFormattedDateString(value, timezone);
  }

  public getActionTakenValue(actionTaken: ActionTakenEnum | undefined, numOccurrencesString: string | undefined) {
    if (actionTaken === undefined || numOccurrencesString === undefined) {
      return '-';
    }
    const label = this.getContentLabelFromValue(actionTaken, ColumnKeys.ACTION_TAKEN);
    return `<span>${label} ${numOccurrencesString} </span>`;
  }

  public getColumnNameFromKey(columnKey: ColumnKeys) {
    const columnDetails = this.allColumnDetails.find((colDetails: ColumnDetails) => colDetails.key === columnKey);

    if (!columnDetails) {
      throw new Error(`Unknown column ${columnKey}`);
    }

    return columnDetails.columnName;
  }

  public buildEndpointNameContent(
    endpoint: RawEventDeviceDetails,
    state: ActionPermissionState
  ): GzLinkWebComponent | HTMLElement {
    const clickHandler = () =>
      linkComponent.dispatchEvent(
        new CustomEvent(DetailsPanelEvents.NAVIGATE_TO_DETAILS_PANEL, {
          bubbles: true,
          composed: true,
          cancelable: true,
          detail: {
            protectedEntityId: endpoint.protectedEntityId,
          },
        })
      );

    const linkComponent = this.getGenericLinkElement(clickHandler, state);
    const contentWithHandledRemovedState = this.handleElementContent(linkComponent, state, endpoint.name);
    return state === ActionPermissionState.MISSING_NETWORK
      ? this.buttonHelper.handleTooltips(contentWithHandledRemovedState, state)
      : contentWithHandledRemovedState;
  }

  public buildPolicyContent(policy: RawEventPolicyDetails, state: ActionPermissionState) {
    const clickHandler = () =>
      linkComponent.dispatchEvent(
        new CustomEvent(DetailsPanelEvents.POLICY_DRILL_DOWN, {
          detail: {
            policyId: policy.policyId,
          },
          bubbles: true,
          composed: true,
          cancelable: true,
        })
      );

    const linkComponent = this.getGenericLinkElement(clickHandler, state);
    const policyContent = this.handleElementContent(linkComponent, state, policy.policyName);
    policyContent.classList.add('link-overflow');
    return this.buttonHelper.handleTooltips(policyContent, state);
  }

  public buildEndpointOnlineStatus(endpoint: RawEventDeviceDetails) {
    return endpoint.isOnline ? this.dependencies.translateFn('Online') : this.dependencies.translateFn('Offline');
  }

  public buildDeviceRiskScore(
    riskManagement: RawEventRiskManagementDetails,
    state: ActionPermissionState,
    drillDownData: SecurityRisksDrillDown,
    section: string
  ) {
    if (!riskManagement.deviceRiskScore) {
      return '-';
    }

    const clickHandler = () =>
      linkComponent.dispatchEvent(
        new CustomEvent(DetailsPanelEvents.SECURITY_RISKS_DRILL_DOWN, {
          detail: {
            ...drillDownData,
            section,
            filters: [
              {
                [DevicesAppColumnKeys.DEVICE_NAME]: {
                  complexEnumFilter: [
                    {
                      label: drillDownData.endpoint.name,
                      value: drillDownData.endpoint.id,
                    },
                  ],
                },
              },
            ],
          },
          bubbles: true,
          composed: true,
          cancelable: true,
        })
      );

    const linkComponent = this.getGenericLinkElement(clickHandler, state);

    let color = '';
    let text = '-';
    switch (riskManagement.routinesSeverity) {
      case RoutinesSeverity.LOW:
        color = 'var(--yellow-alert-050)';
        text = translateFn('Low');
        break;
      case RoutinesSeverity.MEDIUM:
        color = 'var(--orange-alert-chart-100)';
        text = translateFn('Medium');
        break;
      case RoutinesSeverity.HIGH:
        text = translateFn('High');
        color = 'var(--red-alert-500-severe)';
        break;
      default:
        break;
    }

    linkComponent.innerHTML = `<gz-bullet color="${color}"><span>${text} (${riskManagement.deviceRiskScore}%)</span></gz-bullet>`;
    return this.buttonHelper.handleTooltips(linkComponent, state);
  }

  public buildDeviceRisksCount(
    count: number | undefined,
    state: ActionPermissionState,
    drillDownData: SecurityRisksDrillDown,
    section: string
  ) {
    if (!count) {
      return '-';
    }

    const clickHandler = () =>
      linkComponent.dispatchEvent(
        new CustomEvent(DetailsPanelEvents.SECURITY_RISKS_DRILL_DOWN, {
          detail: {
            ...drillDownData,
            section,
            filters: [
              {
                [MisconfigurationAppColumnKeys.AFFECTED_DEVICES]: {
                  complexEnumFilter: [
                    {
                      label: drillDownData.endpoint.name,
                      value: drillDownData.endpoint.id,
                    },
                  ],
                },
              },
            ],
          },
          bubbles: true,
          composed: true,
          cancelable: true,
        })
      );

    const linkComponent = this.getGenericLinkElement(clickHandler, state);
    linkComponent.innerText = count.toString();

    return this.buttonHelper.handleTooltips(linkComponent, state);
  }

  public buildTagsContent(affectedDeviceTags: RawEventTag[] | undefined): string | HTMLElement {
    if (!affectedDeviceTags) {
      return '-';
    }

    const tagNamesContent = document.createElement('div');
    tagNamesContent.setAttribute('part', 'tag-names-content');

    const clipContent = new GzClipContentWebComponent();
    let clipContentText = affectedDeviceTags?.map(tag => htmlEncode(tag.name)).join(', ');
    clipContent.innerHTML = clipContentText;
    clipContent.hasTextActionButton = true;
    clipContent.breakWords = false;

    tagNamesContent.appendChild(clipContent);

    return tagNamesContent;
  }

  private handleElementContent(
    linkComponent: GzLinkWebComponent,
    state: ActionPermissionState,
    name?: string
  ): GzLinkWebComponent | HTMLElement {
    switch (state) {
      case ActionPermissionState.REMOVED:
      case ActionPermissionState.NO_POLICY: {
        linkComponent.innerHTML = `${this.dependencies.translateFn('(removed)')}`;
        linkComponent.color = LinkColor.DARK;
        linkComponent.disabled = true;
        const container = document.createElement('div');
        const entityName = document.createElement('span');
        entityName.textContent = `${name ?? '-'}`;
        container.append(entityName, linkComponent);
        return container;
      }

      case ActionPermissionState.LOST_ACCESS: {
        const entityName = document.createElement('span');
        entityName.textContent = `${name ?? '-'}`;
        return entityName;
      }

      default: {
        linkComponent.textContent = `${name ?? '-'}`;
        return linkComponent;
      }
    }
  }

  private getGenericLinkElement(clickHandler, state): GzLinkWebComponent {
    const linkComponent = new GzLinkWebComponent();

    linkComponent.type = LinkType.QUIET;
    linkComponent.color = LinkColor.PRIMARY;
    linkComponent.callback = this.buttonHelper.isButtonEnabled(state) ? clickHandler : undefined;
    linkComponent.disabled = !this.buttonHelper.isButtonEnabled(state);

    return linkComponent;
  }
}
